/**
 * Section Comparison Component
 *
 * Reusable side-by-side comparison view for displaying original vs AI-generated section content.
 * Used in ApprovalModal and SectionDiffModal.
 */

import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { EditorView } from '../../../components/BlockNote/EditorView';

export interface SectionComparisonProps {
  /**
   * Section name to display in headers
   */
  sectionName: string;

  /**
   * Section ID for unique style IDs
   */
  sectionId: string;

  /**
   * Original section content (markdown)
   */
  originalContent: string;

  /**
   * AI-generated section content (markdown)
   */
  newContent: string;

  /**
   * Optional style ID prefix for EditorView components
   * Defaults to 'section-comparison'
   */
  styleIdPrefix?: string;
}

/**
 * SectionComparison - Side-by-side comparison of original vs AI-generated content
 */
export const SectionComparison: React.FC<SectionComparisonProps> = ({
  sectionName,
  sectionId,
  originalContent,
  newContent,
  styleIdPrefix = 'section-comparison',
}) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 0,
        minHeight: 300,
      }}
    >
      {/* Left Column - Original */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          borderRight: `1px solid ${theme.palette.divider}`,
          overflow: 'hidden',
        }}
      >
        {/* Column Header */}
        <Box
          sx={{
            px: 3,
            py: 2,
            bgcolor: theme.palette.grey[100],
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
            Original
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 700, mt: 0.5 }}>
            {sectionName}
          </Typography>
        </Box>

        {/* Column Content */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
          <EditorView
            markdown={originalContent}
            editable={false}
            sideMenu={false}
            styleId={`${styleIdPrefix}-original-${sectionId}`}
            analytics={{ enabled: false }}
          />
        </Box>
      </Box>

      {/* Right Column - AI Generated */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Column Header */}
        <Box
          sx={{
            px: 3,
            py: 2,
            bgcolor: 'rgba(109, 40, 217, 0.08)',
            borderBottom: `1px solid ${theme.palette.divider}`,
            borderLeft: `3px solid ${theme.palette.primary.main}`,
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'primary.main' }}>
            AI Generated
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 700, mt: 0.5, color: 'text.primary' }}>
            {sectionName}
          </Typography>
        </Box>

        {/* Column Content */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
          <EditorView
            markdown={newContent}
            editable={false}
            sideMenu={false}
            styleId={`${styleIdPrefix}-new-${sectionId}`}
            analytics={{ enabled: false }}
          />
        </Box>
      </Box>
    </Box>
  );
};
